/**
 * @typedef {{
 * 	amountToLoan?: number | null,
 * 	amortizationYears?: number | null,
 * 	dueDate?: Date | string | null,
 * 	escrowInsuranceMonthly?: number | null,
 * 	escrowTaxMonthly?: number | null,
 * 	originalDateOfContract?: Date | string | null,
 * 	rate?: number | null,
 * }} MortgageDetails
 * @typedef {{
 * 	date?: Date | string | null,
 * 	listing_status?: string | null,
 * 	listingStatus?: string | null,
 * 	price?: number | null,
 * }} PriceHistoryEntry
 */

/**
 * @param {Date | string | null | undefined} value
 * @returns {Date | null}
 */
export function parseFinanceWidgetDate(value) {
	if (!value) {
		return null;
	}

	if (value instanceof Date) {
		return Number.isNaN(value.valueOf()) ? null : value;
	}

	const dateOnlyParts = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

	if (dateOnlyParts) {
		const [, year, month, day] = dateOnlyParts;
		const parsedYear = Number(year);
		const parsedMonth = Number(month);
		const parsedDay = Number(day);
		const date = new Date(parsedYear, parsedMonth - 1, parsedDay);

		if (
			Number.isNaN(date.valueOf()) ||
			date.getFullYear() !== parsedYear ||
			date.getMonth() !== parsedMonth - 1 ||
			date.getDate() !== parsedDay
		) {
			return null;
		}

		return date;
	}

	const date = new Date(value);

	return Number.isNaN(date.valueOf()) ? null : date;
}

/**
 * @param {PriceHistoryEntry[] | null | undefined} priceHistory
 * @returns {Date | null}
 */
export function getMostRecentSoldDate(priceHistory) {
	const soldDates = getMostRecentSoldEntries(priceHistory)
		.map((entry) => parseFinanceWidgetDate(entry.date))
		.filter(Boolean);

	return soldDates[0] ?? null;
}

/**
 * @param {PriceHistoryEntry[] | null | undefined} priceHistory
 * @returns {PriceHistoryEntry[]}
 */
export function getMostRecentSoldEntries(priceHistory) {
	return (priceHistory ?? [])
		.filter(
			(entry) =>
				(entry?.listingStatus ?? entry?.listing_status)
					?.toLowerCase()
					.trim() === 'sold'
		)
		.sort(
			(a, b) =>
				(parseFinanceWidgetDate(b.date)?.valueOf() ?? 0) -
				(parseFinanceWidgetDate(a.date)?.valueOf() ?? 0)
		);
}

/**
 * @param {PriceHistoryEntry[] | null | undefined} priceHistory
 * @returns {number | null}
 */
export function getMostRecentSoldPriceInDollars(priceHistory) {
	const soldPriceInCents = getMostRecentSoldEntries(priceHistory).find(
		(entry) => entry.price !== null && entry.price !== undefined
	)?.price;

	return convertFinanceWidgetCentsToDollars(soldPriceInCents);
}

/**
 * @param {Date | string | null | undefined} originationDate
 * @param {PriceHistoryEntry[] | null | undefined} priceHistory
 * @returns {Date | null}
 */
export function resolveFinanceWidgetOriginationDate(
	originationDate,
	priceHistory
) {
	return (
		parseFinanceWidgetDate(originationDate) ??
		getMostRecentSoldDate(priceHistory)
	);
}

/**
 * @param {Date | string | null | undefined} value
 * @returns {string}
 */
export function formatFinanceWidgetDate(value) {
	const date = parseFinanceWidgetDate(value);

	return date ? date.toISOString() : '';
}

/**
 * @param {number | null | undefined} value
 * @returns {string}
 */
export function formatFinanceWidgetNumber(value) {
	return value === null || value === undefined ? '' : String(value);
}

/**
 * @param {number | null | undefined} value
 * @returns {number | null}
 */
export function convertFinanceWidgetCentsToDollars(value) {
	return value === null || value === undefined ? null : value / 100;
}

/**
 * Required by the current widget embed contract for payment-based widgets.
 * @param {number | null | undefined} loanAmount
 * @param {number | null | undefined} annualRate
 * @param {number} amortizationYears
 * @returns {number | null}
 */
export function calculateFinanceWidgetMonthlyPayment(
	loanAmount,
	annualRate,
	amortizationYears
) {
	if (
		loanAmount === null ||
		loanAmount === undefined ||
		annualRate === null ||
		annualRate === undefined ||
		!amortizationYears
	) {
		return null;
	}

	const paymentCount = amortizationYears * 12;
	const monthlyRate = annualRate / 100 / 12;

	if (monthlyRate === 0) {
		return loanAmount / paymentCount;
	}

	const compoundRate = (1 + monthlyRate) ** paymentCount;

	return (loanAmount * monthlyRate * compoundRate) / (compoundRate - 1);
}

/**
 * @param {Date | string | null | undefined} originationDate
 * @param {Date} [currentDate]
 * @returns {number | null}
 */
export function calculateMonthsSinceOrigination(
	originationDate,
	currentDate = new Date()
) {
	if (!originationDate) {
		return null;
	}

	const startDate = parseFinanceWidgetDate(originationDate);

	if (!startDate || startDate > currentDate) {
		return null;
	}

	const yearDifference = currentDate.getFullYear() - startDate.getFullYear();
	const monthDifference = currentDate.getMonth() - startDate.getMonth();
	const monthsFromYears = yearDifference * 12;
	let monthCount = monthsFromYears + monthDifference;

	if (currentDate.getDate() < startDate.getDate()) {
		monthCount -= 1;
	}

	return Math.max(monthCount, 0);
}

/**
 * @param {MortgageDetails} details
 * @param {Date} [currentDate]
 * @returns {number | null}
 */
export function calculateEstimatedCurrentBalance(
	details,
	currentDate = new Date()
) {
	const amortizationYears = details.amortizationYears ?? 30;
	const payment = calculateFinanceWidgetMonthlyPayment(
		details.amountToLoan,
		details.rate,
		amortizationYears
	);
	const monthsSinceOrigination = calculateMonthsSinceOrigination(
		details.originalDateOfContract,
		currentDate
	);

	if (
		details.amountToLoan === null ||
		details.amountToLoan === undefined ||
		details.rate === null ||
		details.rate === undefined ||
		payment === null ||
		monthsSinceOrigination === null
	) {
		return null;
	}

	const monthlyRate = details.rate / 100 / 12;

	if (monthlyRate === 0) {
		const paidPrincipal = payment * monthsSinceOrigination;

		return Math.max(details.amountToLoan - paidPrincipal, 0);
	}

	const compoundRate = (1 + monthlyRate) ** monthsSinceOrigination;
	const compoundedOriginalBalance = details.amountToLoan * compoundRate;
	const compoundedPaidBalance = (payment / monthlyRate) * (compoundRate - 1);
	const balance = compoundedOriginalBalance - compoundedPaidBalance;

	return Math.max(balance, 0);
}

/**
 * @param {Map<string, {valueMean?: number | null, date?: Date | null}[]> | null | undefined} valuations
 * @returns {number | null}
 */
export function getLatestQuantariumValueInDollars(valuations) {
	const quantariumValuations = valuations?.get('quantarium') ?? [];
	const privateLabelValuations =
		valuations?.get('quantariumPrivateLabel') ?? [];
	const validQuantariumValuations = quantariumValuations.filter(
		(valuation) => valuation?.valueMean
	);
	const validValuations = validQuantariumValuations.length
		? validQuantariumValuations
		: privateLabelValuations.filter((valuation) => valuation?.valueMean);

	if (!validValuations.length) {
		return null;
	}

	const latestValuation =
		validValuations
			.filter((valuation) => valuation.date !== null)
			.sort(
				(a, b) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0)
			)[0] ?? validValuations[0];

	return convertFinanceWidgetCentsToDollars(latestValuation.valueMean);
}

/**
 * @param {number | null | undefined} quantariumValue
 * @param {number | null | undefined} currentBalance
 * @returns {number | null}
 */
export function calculateEstimatedCurrentEquity(
	quantariumValue,
	currentBalance
) {
	if (
		quantariumValue === null ||
		quantariumValue === undefined ||
		currentBalance === null ||
		currentBalance === undefined
	) {
		return null;
	}

	return quantariumValue - currentBalance;
}

/**
 * @param {{
 * 	accessToken?: string | null,
 * 	agentId?: string | null,
 * 	details: MortgageDetails,
 * 	estimatedHomeValue?: number | null,
 * 	priceHistory?: PriceHistoryEntry[] | null,
 * 	userId?: string | null,
 * 	userType: 'agent' | 'homeowner',
 * }} data
 * @returns {{attributes: {[key: string]: string}, reloadKey: string}}
 */
export function buildFinanceWidgetConfiguration(data) {
	const { details } = data;
	const originalDateOfContract = resolveFinanceWidgetOriginationDate(
		details.originalDateOfContract,
		data.priceHistory
	);
	const calculationDetails = {
		...details,
		originalDateOfContract,
	};
	const amortizationYears = details.amortizationYears ?? 30;
	const estimatedHomeValue = data.estimatedHomeValue ?? null;
	const purchasePrice =
		getMostRecentSoldPriceInDollars(data.priceHistory) ??
		details.amountToLoan;
	const monthlyPayment = calculateFinanceWidgetMonthlyPayment(
		details.amountToLoan,
		details.rate,
		amortizationYears
	);
	const estimatedCurrentBalance =
		calculateEstimatedCurrentBalance(calculationDetails);
	const mortgageBalance =
		estimatedCurrentBalance ?? details.amountToLoan ?? null;
	const estimatedEquity =
		estimatedHomeValue === null ||
		mortgageBalance === null ||
		mortgageBalance === undefined
			? null
			: Math.max(estimatedHomeValue - mortgageBalance, 0);
	const formatNumber = formatFinanceWidgetNumber;
	const formatDate = formatFinanceWidgetDate;

	return {
		attributes: {
			'data-access-token': data.accessToken ?? '',
			'data-agent': data.agentId ?? '',
			'data-amortization-months': String(amortizationYears * 12),
			'data-api-base-url': `${window.location.origin}/api/v1`,
			'data-average-avm-value': formatNumber(estimatedHomeValue),
			'data-avm-value': formatNumber(estimatedHomeValue),
			'data-current-avm-value': formatNumber(estimatedHomeValue),
			'data-current-balance': formatNumber(mortgageBalance),
			'data-current-interest-rate': formatNumber(details.rate),
			'data-default-extra-payment': '100',
			'data-default-lump-sum-amount': '1000',
			'data-default-refinance-savings': '100',
			'data-estimated-current-balance': formatNumber(
				estimatedCurrentBalance
			),
			'data-estimated-equity': formatNumber(estimatedEquity),
			'data-estimated-home-value': formatNumber(estimatedHomeValue),
			'data-estimated-principal-and-interest':
				formatNumber(monthlyPayment),
			'data-interest-rate': formatNumber(details.rate),
			'data-loan-amount': formatNumber(details.amountToLoan),
			'data-monthly-payment': formatNumber(monthlyPayment),
			'data-mortgage-balance': formatNumber(mortgageBalance),
			'data-mortgage-due-date': formatDate(details.dueDate),
			'data-mortgage-original-date': formatDate(originalDateOfContract),
			'data-original-date': formatDate(originalDateOfContract),
			'data-original-loan-amount': formatNumber(details.amountToLoan),
			'data-purchase-price': formatNumber(purchasePrice),
			'data-refinance-savings-amount': '100',
			'data-tenant': window.location.host,
			'data-tenant-id': window.location.host,
			'data-user-id': data.userId ?? '',
			'data-user-type': data.userType,
		},
		reloadKey: [
			details.amountToLoan,
			details.rate,
			amortizationYears,
			details.escrowTaxMonthly,
			details.escrowInsuranceMonthly,
			formatDate(originalDateOfContract),
			estimatedCurrentBalance,
			estimatedHomeValue,
		].join(':'),
	};
}
